import { useEffect, useRef, useState } from "react";
import { usePharmaFormFields } from "../../../context/pharmaFormFieldsContext";
import {
  FormDataModel,
  FormField,
} from "../../../data-model/pharmaPaFormModel";
import EyeIcon from "../../../svg/eye";

import { useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import RenderFields from "../../../components/formFields/renderFields";
import RenderSectionHeader from "../../../components/formFields/renderSectionHeader";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModal";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { RootState } from "../../../redux/store/store";
import WarningIcon from "../../../svg/warningIcon";
import {
  createNestedObjectFromDotKeys,
  getArrayChildren,
} from "../../../utils/createObjectFromDotNotation";
import {
  deleteNestedValue,
  getNestedValue,
  setNestedValue,
} from "../../../utils/getNestedValue";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { replaceKeyOfArrayTypeFieldWithCorrectIndex } from "../../../utils/replaceKeyOfArrayTypeFieldWithCorrectIndex";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { extractFieldMappings } from "../../pharmaPaForm/utils/getFormFieldsInfo";
import { replaceOptionsWithRefDocId } from "../../pharmaPaForm/utils/updateFormOptions";
import { createOrderDataFromContextFormData } from "./utils/createOrderDataFromContextFormData";

interface MedicalPaFormProps {
  sectionOnClick?: (sectionKey: string) => void;
  docId: string;
  scrollSectionId?: (sectionKey: string) => void;
  formConfiguration?: FormDataModel;
}

const MedicalPaForm = (props: MedicalPaFormProps) => {
  const { sectionOnClick, scrollSectionId } = props;
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { data: formOptions } = useSelector(
    (state: RootState) => state.formOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const [formConfig, setFormConfig] = useState<FormDataModel | null>(null);
  const [localMedicalPaOrder, setLocalMedicalPaOrder] =
    useState<MedicalPaOrder | null>(null);
  const [originalMedicalPaOrder, setOriginalMedicalPaOrder] =
    useState<MedicalPaOrder | null>(null);
  const { formFieldsData, setFormFieldsData, isFormDirty, setFormDirty } =
    usePharmaFormFields();
  const [docViewerModalOpen, setDocViewerModalOpen] = useState<boolean>(false);
  const [docDownloadUrl, setDocDownloadUrl] = useState<string>("");

  const hasLoggedToBigQuery = useRef(false);
  const orgId = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  useEffect(() => {
    if (singleMedicalPaOrder) {
      const data = { ...singleMedicalPaOrder };
      const hpcsCodes = data?.hpcs_codes ?? [];
      const primaryHpcsCode = hpcsCodes.filter(
        (hpcs) => hpcs.auth_status === MedicalPaOrdersAuthStatus.AuthRequired,
      );
      data.hpcs_codes = primaryHpcsCode ?? [];
      setOriginalMedicalPaOrder(data ?? null);
      setLocalMedicalPaOrder(data ?? null);
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (props.formConfiguration && originalMedicalPaOrder) {
      const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
        props.formConfiguration ?? null,
        originalMedicalPaOrder,
      );

      setFormConfig(
        replaceOptionsWithRefDocId(
          updatedFormConfig ?? null,
          formOptions ?? [],
        ),
      );
    }
  }, [props.formConfiguration, originalMedicalPaOrder]);

  useEffect(() => {
    const observedSections = document.querySelectorAll(
      ".single-section__container",
    );

    const root = document.querySelector(
      ".main-form.medical-pharma-pa-form__body",
    );

    if (observedSections.length === 0) return;
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0.9) {
          scrollSectionId?.(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: root,
      threshold: [0.5, 0.9],
    });

    observedSections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [formConfig]);

  useEffect(() => {
    if (originalMedicalPaOrder && formConfig) {
      const fieldMappings = extractFieldMappings(formConfig?.fields ?? []);
      const updatedFormFieldsData = Object.entries(fieldMappings).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: {
            ...(acc[key] || {
              isRequired: value?.isRequired ?? false,
              regexMatcher: value?.regexMatcher ?? null,
              type: value?.type ?? "",
            }),
            filledValue: !isFormDirty
              ? getNestedValue(originalMedicalPaOrder, key, value?.default)
              : getNestedValue(localMedicalPaOrder, key, value?.default),
            isFieldDirty: isFormDirty ?? false,
            default: value?.default ?? null,
          },
        }),
        {},
      );

      setFormFieldsData(updatedFormFieldsData);
      if (!hasLoggedToBigQuery.current) {
        hasLoggedToBigQuery.current = true;

        apiWorkerManager
          .enqueueAndProcess({
            patient_id: singleMedicalPaOrder?.patient_id ?? "",
            order_id: singleMedicalPaOrder?.order_id ?? "",
            org_id: orgId ?? "",
            event_name: EventsName.FORM_SCREEN_SHOWN,
            user_id: user?.id ?? "",
            user_email: user?.email ?? "",
            additional_data: {
              screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
              source: ScreenNamesForBigQuery.FORM_SCREEN,
              order_status:
                singleMedicalPaOrder?.status?.financial_review ?? "",
              auth_status:
                singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
              payer_name: payerName,
            },
          })
          .catch((error) => {
            console.error("Failed to log event to BigQuery:", error);
          });
      }
    }
  }, [formConfig, singleMedicalPaOrder, localMedicalPaOrder]);

  useEffect(() => {
    if (singleMedicalPaOrder) {
      const clinicalAttachmentDocPath =
        singleMedicalPaOrder?.clinical_document?.file_path ?? "";
      if (clinicalAttachmentDocPath !== "") {
        getFileDownloadUrl(clinicalAttachmentDocPath ?? "").then((url) => {
          setDocDownloadUrl(url);
        });
      }
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (docViewerModalOpen) {
      openModal("doc-viewer-modal");
    }
  }, [docViewerModalOpen]);

  const onAddMore = (key: string) => {
    const data = createOrderDataFromContextFormData(
      formFieldsData,
      singleMedicalPaOrder ?? {},
    );
    const keys = getArrayChildren(Object.keys(formFieldsData), key);
    const objectWithKeysAndValues = keys?.reduce((acc, key) => {
      acc[key] = formFieldsData[key].filledValue;
      return acc;
    }, {});
    const oldUpdatedData = createNestedObjectFromDotKeys(
      objectWithKeysAndValues ?? {},
    );
    const updatedData = [...oldUpdatedData[key], {}];
    const currentDataInMedicalPaOrder = setNestedValue(
      data,
      key,
      updatedData,
      true,
    );
    const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
      formConfig ?? { fields: [], formTitle: "" },
      currentDataInMedicalPaOrder,
    );

    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.ADD_MORE_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        source: key,
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
    setFormConfig(updatedFormConfig);
    setLocalMedicalPaOrder(currentDataInMedicalPaOrder);
    setFormDirty(true);
  };

  const onDelete = (path: string, parentKey: string) => {
    let data = createOrderDataFromContextFormData(
      formFieldsData,
      singleMedicalPaOrder ?? {},
    );
    const keys = getArrayChildren(Object.keys(formFieldsData), parentKey);
    const objectWithKeysAndValues = keys?.reduce((acc, key) => {
      acc[key] = formFieldsData[key].filledValue;
      return acc;
    }, {});
    const oldUpdatedData = createNestedObjectFromDotKeys(
      objectWithKeysAndValues ?? {},
    );
    data = setNestedValue(data, parentKey, oldUpdatedData[parentKey], true);
    const currentDataInMedicalPaOrder = deleteNestedValue(data, path);
    const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
      formConfig ?? { fields: [], formTitle: "" },
      currentDataInMedicalPaOrder,
    );

    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.DELETE_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        source: parentKey,
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
    setFormConfig(updatedFormConfig);
    setLocalMedicalPaOrder(currentDataInMedicalPaOrder);
    setFormDirty(true);
  };

  const renderEachSectionOfTheForm = (
    section: FormField,
    onClick?: (sectionKey: string) => void,
  ) => {
    if (section.type !== "section" || !("fields" in section)) {
      return null;
    }

    const groupedFields = section.fields.reduce<Record<number, FormField[]>>(
      (acc, field) => {
        const rowIndex = field.rowIndex ?? 0;
        if (!acc[rowIndex]) {
          acc[rowIndex] = [];
        }
        acc[rowIndex].push(field);
        return acc;
      },
      {},
    );

    return (
      <div
        key={section.key}
        id={`${section.key}`}
        className="single-section__container mb-6"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(section.key);
        }}
      >
        <RenderSectionHeader
          sectionTitle={section.label}
          sectionKey={`${section.key}`}
          id={`${section.key}`}
        />
        <div>
          {Object.keys(groupedFields).map((rowIndex) => (
            <div
              key={rowIndex}
              className="single-row__container mb-2 flex gap-2"
            >
              {groupedFields[rowIndex]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((field) => (
                  <RenderFields
                    field={field}
                    key={field.key}
                    data={localMedicalPaOrder}
                    originalData={originalMedicalPaOrder}
                    onAddMore={onAddMore}
                    onDelete={onDelete}
                    shouldEmitLogEvent={true}
                  />
                ))}
            </div>
          ))}
        </div>
        {(section.additionalInfoHeader || section.additionalInfoContent) && (
          <div className="section-additonal-info flex w-full flex-col gap-2 bg-secondaryYellow-11 p-3">
            <div className="section-additonal-info__header text-tiny font-bold">
              {section.additionalInfoHeader ?? ""}
            </div>
            <div className="section-additonal-info__content text-[0.8125rem] font-normal">
              {section.additionalInfoContent ?? ""}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {formConfig &&
        Object.keys(formConfig).length > 0 &&
        formFieldsData &&
        Object.keys(formFieldsData).length > 0 && (
          <div className="pharma-form__container">
            {formConfig?.fields
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((section) =>
                renderEachSectionOfTheForm(section, sectionOnClick),
              )}
            <div
              key={"attachments"}
              id={"attachments"}
              className="single-section__container mb-6"
              onClick={(e) => {
                e.stopPropagation();
                sectionOnClick?.("attachments");
              }}
            >
              <RenderSectionHeader
                sectionTitle={"Attachments"}
                sectionKey={"attachments"}
                id={"attachments"}
              />
              {singleMedicalPaOrder?.clinical_document?.file_path ? (
                <>
                  <div
                    className="document-card flex w-full justify-between rounded border border-primaryGray-15 px-3 py-2 text-small font-semibold shadow hover:cursor-pointer"
                    onClick={() => {
                      setDocViewerModalOpen(true);

                      apiWorkerManager.enqueueAndProcess({
                        patient_id: singleMedicalPaOrder?.patient_id ?? "",
                        order_id: singleMedicalPaOrder?.order_id ?? "",
                        org_id: orgId ?? "",
                        event_name: EventsName.CLINICAL_DOCUMENT_CLICKED,
                        user_id: user?.id ?? "",
                        user_email: user?.email ?? "",
                        additional_data: {
                          form_name: formConfig?.formTitle ?? "",
                          screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
                          payer_name: payerName,
                        },
                      });
                    }}
                  >
                    <div className="document-card--text">Clinical Document</div>
                    <div className="document-card--icon flex items-center gap-1 text-tertiaryBlue-4">
                      <EyeIcon />
                      View Document
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {" "}
                  <div className="document-card flex w-full items-center gap-2 px-3 py-2 text-small font-normal text-tertiaryRed-3">
                    <div className="document-card--info">
                      <WarningIcon height="16" width="16" />
                    </div>
                    <div className="document-card--text">
                      No Attachments Found
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      {docViewerModalOpen && (
        <DocViewerModal
          fileUrl={docDownloadUrl}
          onClose={() => {
            setDocViewerModalOpen(false);
          }}
          type={"clinical_attachment"}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.FORM_SCREEN}
          formName={formConfig?.formTitle ?? ""}
        />
      )}
    </>
  );
};

export default MedicalPaForm;
